import { Component, EventEmitter, OnInit, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChipModule } from 'primeng/chip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { IFilterDropdownsForm } from '@core/models';
import { STATUS_OPTIONS, BOOLEAN_OPTIONS } from '@shared/components/constants/filter-options.contants';
import { CodeFilters, AppliedFilter, ModifierOption, StatusOption, BooleanOption } from '@shared/components/models/code-filters.model';

@Component({
  selector: 'app-code-filters',
  templateUrl: './code-filters.component.html',
  styleUrls: ['./code-filters.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    MultiSelectModule,
    ChipModule,
    IconFieldModule,
    InputIconModule
  ]
})
export class CodeFiltersComponent implements OnInit, OnChanges {
  @Input() selectedFilters: IFilterDropdownsForm | null = null;
  @Input() showOnlyChips = false;
  @Input() showOnlyPanel = false;
  @Input() refreshTrigger?: number;
  @Output() filtersApplied = new EventEmitter<CodeFilters>();

  codeFilterForm!: FormGroup;
  isFilterVisible = false;
  appliedFilters: AppliedFilter[] = [];

  modifierOptions: ModifierOption[] = [];
  statusOptions: StatusOption[] = STATUS_OPTIONS;
  booleanOptions: BooleanOption[] = BOOLEAN_OPTIONS;
  isLoadingModifiers = false;

  constructor(
    private fb: FormBuilder,
    private codeManagementApiService: CodeManagementApiService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadModifiers();
    this.clearAllFilters();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshTrigger']?.firstChange) return;
    if (changes['refreshTrigger']) {
      this.loadAppliedFiltersFromStorage();
    }
  }
  
  private loadAppliedFiltersFromStorage(): void {
    this.appliedFilters = this.getFromStorage<AppliedFilter[]>("codeManagementAppliedFilters") || [];
    
    const savedFilters = this.getFromStorage<CodeFilters>("codeManagementFilters");
    if (savedFilters) {
      this.loadFormFromFilters(savedFilters);
    } else {
      this.codeFilterForm.reset();
    }
  }
  
  private getFromStorage<T>(key: string): T | null {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }
  
  private saveToStorage(key: string, value: any): void {
    sessionStorage.setItem(key, JSON.stringify(value));
  }
  
  private loadFormFromFilters(filters: CodeFilters): void {
    const formValue = this.getDefaultFormValues();
    
    Object.assign(formValue, {
      code: filters.code || '',
      shortDescription: filters.shortDescription || '',
      modifiers: filters.modifiers || [],
      delegated: filters.delegated ?? null,
      paRequired: filters.paRequired ?? null,
      paAllowed: filters.paAllowed ?? null,
      status: filters.status || null,
      formId: filters.formId || '',
      formTitle: filters.formTitle || '',
      sharedForm: filters.sharedForm ?? null,
      formEffectiveStartDate: filters.formEffectiveStartDateFrom ? new Date(filters.formEffectiveStartDateFrom) : null
    });
    
    formValue.cmsEffectiveStartDateRange = this.buildDateRange(
      filters.cmsEffectiveStartDateFrom, 
      filters.cmsEffectiveStartDateTo
    );
    formValue.planEffectiveStartDateRange = this.buildDateRange(
      filters.planEffectiveStartDateFrom, 
      filters.planEffectiveStartDateTo
    );
    formValue.modifierEffectiveStartDateRange = this.buildDateRange(
      filters.modifierEffectiveStartDateFrom, 
      filters.modifierEffectiveStartDateTo
    );
    
    this.codeFilterForm.reset(formValue, { emitEvent: false });
  }
  
  private buildDateRange(start: any, end: any): [Date | null, Date | null] | null {
    if (!start && !end) return null;
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null; 
    return [startDate, endDate];
  }

  private loadModifiers(): void {
    this.isLoadingModifiers = true;
    this.codeManagementApiService.getModifiers().subscribe({
      next: (modifiers) => {
        this.modifierOptions = modifiers.map(modifier => ({
          label: modifier.modifierName,
          value: modifier.modifierName
        }));
        this.isLoadingModifiers = false;
      },
      error: (error) => {
        console.error('Error loading modifiers:', error);
        this.isLoadingModifiers = false;
        this.modifierOptions = [];
      }
    });
  }

  private initializeForm(): void {
    this.codeFilterForm = this.fb.group({
      code: [''],
      shortDescription: [''],
      cmsEffectiveStartDateRange: [null],
      planEffectiveStartDateRange: [null],
      modifiers: [[]],
      modifierEffectiveStartDateRange: [null],
      delegated: [null],
      paRequired: [null],
      paAllowed: [null],
      status: [null],
      formId: [''],
      formTitle: [''],
      sharedForm: [null],
      formEffectiveStartDate: [null]
    });
  }
  
  private getDefaultFormValues(): any {
    const tempForm = this.fb.group({
      code: [''],
      shortDescription: [''],
      cmsEffectiveStartDateRange: [null],
      planEffectiveStartDateRange: [null],
      modifiers: [[]],
      modifierEffectiveStartDateRange: [null],
      delegated: [null],
      paRequired: [null],
      paAllowed: [null],
      status: [null],
      formId: [''],
      formTitle: [''],
      sharedForm: [null],
      formEffectiveStartDate: [null]
    });
    return tempForm.value;
  }

  toggleFilterVisibility(): void {
    this.isFilterVisible = !this.isFilterVisible;
    
    if (this.isFilterVisible) {
      this.loadAppliedFiltersFromStorage();
    }
  }

  applyFilters(): void {
    const formValue = this.codeFilterForm.value;
    const filters: CodeFilters = {};

    // Handle text and select fields
    if (formValue.code) filters.code = formValue.code;
    if (formValue.shortDescription) filters.shortDescription = formValue.shortDescription;
    if (formValue.modifiers && formValue.modifiers.length > 0) filters.modifiers = formValue.modifiers;
    if (formValue.delegated !== null && formValue.delegated !== undefined) filters.delegated = formValue.delegated;
    if (formValue.paRequired !== null && formValue.paRequired !== undefined) filters.paRequired = formValue.paRequired;
    if (formValue.paAllowed !== null && formValue.paAllowed !== undefined) filters.paAllowed = formValue.paAllowed;
    if (formValue.status) filters.status = formValue.status;
    if (formValue.formId) filters.formId = formValue.formId;
    if (formValue.formTitle) filters.formTitle = formValue.formTitle;
    if (formValue.sharedForm !== null && formValue.sharedForm !== undefined) filters.sharedForm = formValue.sharedForm;

    // Handle range date pickers - extract start and end dates
    if (formValue.cmsEffectiveStartDateRange && Array.isArray(formValue.cmsEffectiveStartDateRange)) {
      if (formValue.cmsEffectiveStartDateRange[0]) filters.cmsEffectiveStartDateFrom = formValue.cmsEffectiveStartDateRange[0];
      if (formValue.cmsEffectiveStartDateRange[1]) filters.cmsEffectiveStartDateTo = formValue.cmsEffectiveStartDateRange[1];
    }
    if (formValue.planEffectiveStartDateRange && Array.isArray(formValue.planEffectiveStartDateRange)) {
      if (formValue.planEffectiveStartDateRange[0]) filters.planEffectiveStartDateFrom = formValue.planEffectiveStartDateRange[0];
      if (formValue.planEffectiveStartDateRange[1]) filters.planEffectiveStartDateTo = formValue.planEffectiveStartDateRange[1];
    }
    if (formValue.modifierEffectiveStartDateRange && Array.isArray(formValue.modifierEffectiveStartDateRange)) {
      if (formValue.modifierEffectiveStartDateRange[0]) filters.modifierEffectiveStartDateFrom = formValue.modifierEffectiveStartDateRange[0];
      if (formValue.modifierEffectiveStartDateRange[1]) filters.modifierEffectiveStartDateTo = formValue.modifierEffectiveStartDateRange[1];
    }

    // Form Effective Start Date (single date, not range)
    if (formValue.formEffectiveStartDate) {
      filters.formEffectiveStartDateFrom = formValue.formEffectiveStartDate;
    }

    this.appliedFilters = this.buildAppliedFiltersArray(formValue);
    sessionStorage.setItem('codeManagementFilters', JSON.stringify(filters));
    sessionStorage.setItem('codeManagementAppliedFilters', JSON.stringify(this.appliedFilters));
    this.filtersApplied.emit(filters);
    this.isFilterVisible = false;
  }

  cancelFilters(): void {
    this.codeFilterForm.reset();
    this.isFilterVisible = false;
  }

  clearAllFilters(): void {
    this.codeFilterForm.reset();
    this.appliedFilters = [];
    sessionStorage.removeItem('codeManagementFilters');
    sessionStorage.removeItem('codeManagementAppliedFilters');
    this.filtersApplied.emit({});
  }

  removeFilter(filter: AppliedFilter): void {
    const resetValue = filter.key === 'modifiers' ? [] : null;
    this.codeFilterForm.patchValue({ [filter.key]: resetValue }, { emitEvent: false });
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return this.appliedFilters.length > 0;
  }

  get isApplyEnabled(): boolean {
    const formValue = this.codeFilterForm.value;
    return Object.values(formValue).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== '' && value !== undefined;
    });
  }

  private buildAppliedFiltersArray(formValue: any): AppliedFilter[] {
    const filters: AppliedFilter[] = [];

    if (formValue.code) filters.push({ key: 'code', label: 'Code', value: formValue.code });
    if (formValue.shortDescription) filters.push({ key: 'shortDescription', label: 'Short Description', value: formValue.shortDescription });
    if (formValue.modifiers && formValue.modifiers.length > 0) filters.push({ key: 'modifiers', label: 'Modifiers', value: formValue.modifiers.join(', ') });
    if (formValue.delegated !== null && formValue.delegated !== undefined) filters.push({ key: 'delegated', label: 'Delegated', value: formValue.delegated ? 'Yes' : 'No' });
    if (formValue.paRequired !== null && formValue.paRequired !== undefined) filters.push({ key: 'paRequired', label: 'PA Required', value: formValue.paRequired ? 'Yes' : 'No' });
    if (formValue.paAllowed !== null && formValue.paAllowed !== undefined) filters.push({ key: 'paAllowed', label: 'PA Allowed', value: formValue.paAllowed ? 'Yes' : 'No' });
    if (formValue.status) filters.push({ key: 'status', label: 'Status', value: formValue.status });
    if (formValue.formId) filters.push({ key: 'formId', label: 'Form ID', value: formValue.formId });
    if (formValue.sharedForm !== null && formValue.sharedForm !== undefined) filters.push({ key: 'sharedForm', label: 'Shared Form', value: formValue.sharedForm ? 'Yes' : 'No' });
    if (formValue.formTitle) filters.push({ key: 'formTitle', label: 'Form Title', value: formValue.formTitle });

    // Add date range filters
    if (formValue.cmsEffectiveStartDateRange && Array.isArray(formValue.cmsEffectiveStartDateRange)) {
      const [start, end] = formValue.cmsEffectiveStartDateRange;
      if (start || end) {
        const dateStr = this.formatDateRangeForDisplay(start, end);
        filters.push({ key: 'cmsEffectiveStartDateRange', label: 'CMS Effective Date', value: dateStr });
      }
    }
    if (formValue.planEffectiveStartDateRange && Array.isArray(formValue.planEffectiveStartDateRange)) {
      const [start, end] = formValue.planEffectiveStartDateRange;
      if (start || end) {
        const dateStr = this.formatDateRangeForDisplay(start, end);
        filters.push({ key: 'planEffectiveStartDateRange', label: 'Plan Effective Date', value: dateStr });
      }
    }
    if (formValue.modifierEffectiveStartDateRange && Array.isArray(formValue.modifierEffectiveStartDateRange)) {
      const [start, end] = formValue.modifierEffectiveStartDateRange;
      if (start || end) {
        const dateStr = this.formatDateRangeForDisplay(start, end);
        filters.push({ key: 'modifierEffectiveStartDateRange', label: 'Modifier Effective Date', value: dateStr });
      }
    }
    if (formValue.formEffectiveStartDate) {
      const dateStr = this.formatDateForDisplay(formValue.formEffectiveStartDate);
      filters.push({ key: 'formEffectiveStartDate', label: 'Form Effective Start Date', value: dateStr });
    }

    return filters;
  }

  private formatDateRangeForDisplay(start: Date | null, end: Date | null): string {
    const startStr = start ? this.formatDateForDisplay(start) : '';
    const endStr = end ? this.formatDateForDisplay(end) : '';
    if (startStr && endStr) return `${startStr} - ${endStr}`;
    if (startStr) return `From ${startStr}`;
    if (endStr) return `To ${endStr}`;
    return '';
  }

  private formatDateForDisplay(date: Date): string {
    if (!date) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }
}
